import React, { useCallback, useState } from 'react';
import { TextField, Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert } from '@mui/material';
import services from 'services';
import { useLoader } from 'components/loaders/FadeLoader';
import { useMessage } from 'studio/components/Message';
import { useHistory } from 'react-router-dom';
import { trackEvent } from 'utils/eventTracking';
import { generateDefaultBranchName } from 'utils/branchUtils';

export const useCreateDevKg = (knowledgeGraph, fetchKnowledgeGraphs, user, mainKgId = null) => {
  const { successMessage } = useMessage();
  const { withLoader } = useLoader();
  const history = useHistory();

  const [showModal, setShowModal] = useState(false);
  const [branchName, setBranchName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [validationError, setValidationError] = useState('');

  const openModal = useCallback(() => {
    if (knowledgeGraph?.data?.id) {
      setBranchName(generateDefaultBranchName(user));
      setValidationError('');
      setShowModal(true);

      trackEvent('KG Branch Creation Modal Opened', {
        kgId: knowledgeGraph.data.id,
        userType: user?.isSuperUser ? 'superuser' : user?.isAdmin ? 'admin' : 'user',
        context: 'studio'
      });
    }
  }, [knowledgeGraph, user]);

  const closeModal = useCallback(() => {
    setShowModal(false);
    setBranchName('');
    setValidationError('');
    setIsCreating(false);
  }, []);

  const handleCreateBranch = useCallback(async () => {
    const trimmedName = branchName.trim();
    if (!trimmedName) {
      setValidationError('Branch name is required');
      return;
    }

    // Apply same length limit as KG names (28 characters)
    if (trimmedName.length > 28) {
      setValidationError('Branch name must not exceed 28 characters');
      return;
    }

    const kgIdToUse = mainKgId || knowledgeGraph?.data?.id;
    if (!kgIdToUse) return;

    setIsCreating(true);
    setValidationError('');

    try {
      await withLoader(
        services.createDevKnowledgeGraph(kgIdToUse, trimmedName).then(async result => {
          if (result.success) {
            trackEvent('KG Branch Created', {
              branchName: trimmedName,
              kgId: kgIdToUse,
              mainKgId: mainKgId || knowledgeGraph.data.id,
              userType: user?.isSuperUser ? 'superuser' : user?.isAdmin ? 'admin' : 'user',
              context: 'studio'
            });

            successMessage('The development branch has been successfully created');
            await fetchKnowledgeGraphs();
            const params = new URLSearchParams(location.search);
            params.set('id', result?.data);
            history.replace({
              pathname: location.pathname,
              search: '?' + params.toString(),
              hash: location.hash
            });
            closeModal();
            return;
          }
          const message =
            result?.response?.data?.data?.message || 'There was an error creating the development knowledge graph';
          setValidationError(message);
        })
      );
    } catch (error) {
      console.error('Failed to create branch:', error);

      trackEvent('KG Branch Creation Failed', {
        branchName: trimmedName,
        kgId: kgIdToUse,
        mainKgId: mainKgId || knowledgeGraph.data.id,
        error: error.message,
        userType: user?.isSuperUser ? 'superuser' : user?.isAdmin ? 'admin' : 'user',
        context: 'studio'
      });

      setValidationError('Failed to create branch');
    } finally {
      setIsCreating(false);
    }
  }, [
    branchName,
    knowledgeGraph,
    successMessage,
    history,
    withLoader,
    fetchKnowledgeGraphs,
    closeModal,
    user,
    mainKgId
  ]);

  const CreateDevKgModal = () => (
    <Dialog
      open={showModal}
      onClose={!isCreating ? closeModal : undefined}
      maxWidth="sm"
      fullWidth
      data-test="createDevKgDialog"
    >
      <DialogTitle>Create Development Branch</DialogTitle>
      <DialogContent>
        <p style={{ marginBottom: '16px', color: 'rgba(0, 0, 0, 0.6)', fontSize: '0.875rem', lineHeight: 1.5 }}>
          Create a branch (an independent copy) from the main knowledge graph to develop and test changes without
          disturbing other users. It will be connected to the main knowledge graph and have operations to sync changes.
          There can be multiple development branches for the same knowledge graph.
        </p>
        <TextField
          autoFocus
          margin="dense"
          label="Branch Name"
          value={branchName}
          onChange={e => {
            setBranchName(e.target.value);
            if (validationError) setValidationError('');
          }}
          onFocus={event => {
            event.target.select();
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' && !isCreating) {
              handleCreateBranch();
            }
          }}
          placeholder="Enter branch name"
          disabled={isCreating}
          error={!!validationError}
          helperText={validationError || `${(branchName || '').length}/28 characters`}
          inputProps={{ maxLength: 28 }}
          fullWidth
          variant="outlined"
        />
        {validationError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {validationError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={closeModal} disabled={isCreating}>
          Cancel
        </Button>
        <Button onClick={handleCreateBranch} variant="contained" disabled={isCreating}>
          {isCreating ? 'Creating...' : 'Create Branch'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return {
    createDevKnowledgeGraph: openModal,
    CreateDevKgModal
  };
};
